import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

/**
 * Whether the signed-in user may use this client, which is Pro-only.
 *
 * Four states, not a boolean. `unavailable` is the one that matters: a failed check is not
 * evidence of non-entitlement, and treating it as one would show a paying subscriber the
 * upgrade screen they already paid to get past. iOS makes the same distinction for the same
 * reason (`RevenueCatService.EntitlementStatus`), and leaves its own `isPro` untouched when
 * a check fails.
 */
export type EntitlementStatus = 'checking' | 'pro' | 'free' | 'unavailable';

/**
 * The browser has no RevenueCat SDK, so entitlement comes from a callable that asks
 * RevenueCat's REST API server-side. It answers only for the caller, takes no arguments,
 * and fails closed — see `getEntitlement.ts` in zsoogi-ios/firebase-functions.
 */
const callGetEntitlement = httpsCallable<void, { isPro: boolean }>(functions, 'getEntitlement');

export interface Entitlement {
  status: EntitlementStatus;
  /** Set only when `status` is `unavailable`, for a message the user can act on. */
  error: string | null;
  /** Re-run the check — for the retry button, and for "I just subscribed on my phone". */
  recheck: () => void;
}

/**
 * Resolve the Pro entitlement for `uid`.
 *
 * Keyed on the uid rather than run once: signing out and back in as somebody else must
 * re-ask, never inherit the previous account's verdict.
 *
 * Deliberately not cached in localStorage. iOS caches its last confirmed answer so a cold
 * launch with no network does not greet a subscriber with the free tier — a real problem
 * for an app that opens offline. This client cannot load at all without the network that
 * the check needs, so a cache would buy nothing, and `unavailable` already covers the case
 * the iOS cache exists to cover.
 */
export function useEntitlement(uid: string | undefined): Entitlement {
  const [status, setStatus] = useState<EntitlementStatus>('checking');
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!uid) {
      // No session to ask about. App.tsx renders SignIn in this case and never reads this.
      setStatus('checking');
      setError(null);
      return;
    }

    let cancelled = false;
    setStatus('checking');
    setError(null);

    callGetEntitlement()
      .then((result) => {
        if (cancelled) return;
        setStatus(result.data.isPro ? 'pro' : 'free');
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        // Every failure path ends in a visible message, as everywhere else in this client.
        const code = (e as { code?: string }).code ?? '';
        setStatus('unavailable');
        setError(`${(e as Error).message}${code ? ` (${code})` : ''}`);
      });

    return () => {
      cancelled = true;
    };
  }, [uid, attempt]);

  const recheck = useCallback(() => setAttempt((n) => n + 1), []);

  return { status, error, recheck };
}
